<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'searchForm',
})
</script>
<template>
	<el-form ref="queryRef" :inline="inline" :model="props.form" @keyup.enter="searchBtn()">
		<el-form-item v-for="item in props.config" :key="item" :prop="item.name">
			<el-input v-if="item.type == 'input'" v-model="data[item.name]" :placeholder="item.placeholder" @input="handleValueChange"></el-input>
			<el-select
				v-if="item.type == 'select'"
				v-model="data[item.name]"
				:placeholder="item.placeholder"
				:clearable="item?.clearable ?? false"
				:filterable="item?.filterable ?? false"
				:multiple="item?.multiple ?? false"
				:collapse-tags="item?.collapseTags ?? false"
				@change="handleValueChange"
			>
				<el-option
					v-for="i of item.options"
					:key="i[item.props?.value ?? 'value']"
					:value="i[item.props?.value ?? 'value']"
					:label="i[item.props?.label ?? 'label']"
				>
				</el-option>
			</el-select>

			<el-date-picker
				v-if="item.type == 'date'"
				v-model="data[item.name]"
				:type="item.dateType || 'date'"
				:placeholder="item.placeholder"
				:value-format="dateFormat"
				@change="(val: string) => handleDateChange(val, item)"
			></el-date-picker>
			<ma-dict-select
				v-if="item.type == 'dict'"
				v-model="data[item.name]"
				:placeholder="item.placeholder"
				:clearable="item?.clearable ?? false"
				:filterable="item?.filterable ?? false"
				:allow-create="item?.allowCreate ?? false"
				:dict-type="item.dict"
				@change="handleValueChange"
			/>
			<ma-org-select
				v-if="item.type == 'org'"
				v-model="data[item.name]"
				:placeholder="item.placeholder"
				:clearable="item?.clearable ?? false"
				:filterable="item?.filterable ?? false"
				@change="handleValueChange"
			>
			</ma-org-select>
			<ma-org-inspect-select
				v-if="item.type == 'orgBiz'"
				v-model="data[item.name]"
				:placeholder="item.placeholder"
				:clearable="item?.clearable ?? false"
				:filterable="item?.filterable ?? false"
				:check-strictly="item?.checkStrictly ?? false"
				@change="handleValueChange"
			></ma-org-inspect-select>
			<el-tree-select
				v-if="item.type == 'treeselect'"
				v-model="data[item.name]"
				:data="item.options"
				:props="item.props || { label: 'label', value: 'value', children: 'children' }"
				:placeholder="item.placeholder"
				:clearable="item?.clearable ?? false"
				:filterable="item?.filterable ?? false"
				:check-strictly="item?.checkStrictly ?? true"
				style="width: 100%"
				@change="handleValueChange"
			/>
		</el-form-item>

		<el-form-item>
			<el-button icon="Search" type="primary" @click="searchBtn()">{{ t('componentsModule.common.searchForm.query') }}</el-button>
			<el-button icon="RefreshRight" @click="resetBtn()">{{ t('componentsModule.common.searchForm.reset') }}</el-button>
		</el-form-item>
	</el-form>
</template>
<script setup lang="ts" name="searchForm">
import { ref, onMounted, defineProps, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getSearchConfig } from '@/utils/appConfig'
import dayjs from 'dayjs'

const { t } = useI18n()

const dateFormat = 'YYYY-MM-DD HH:mm:ss'
const props = defineProps({
	form: {
		type: Object,
		default: null
	},
	config: {
		type: Array as () => any,
		default: null
	},
	inline: {
		type: Boolean,
		default: true
	}
})

// 获取全局搜索配置
const searchConfig = computed(() => {
	return getSearchConfig()
})

const queryRef = ref()
let searchTimer: number | null = null

const emit = defineEmits(['search', 'reset'])
const searchBtn = () => {
	emit('search')
}
const resetBtn = () => {
	queryRef.value.resetFields()
	emit('reset', queryRef.value)
}

const debounceSearch = () => {
	if (searchTimer) {
		clearTimeout(searchTimer)
	}

	searchTimer = window.setTimeout(() => {
		searchBtn()
		searchTimer = null
	}, searchConfig.value.debounceTime || 2000)
}

const handleValueChange = () => {
	if (searchConfig.value.autoSearch) {
		debounceSearch()
	}
}

const handleDateChange = (val: string, item: any) => {
	if (item.name === 'endTime' && val) {
		data.value[item.name] = dayjs(val).endOf('day').format(dateFormat)
	}

	handleValueChange()
}

const handleDictChange = (originalChange: Function | undefined, val: unknown) => {
	if (originalChange) {
		originalChange(val)
	}

	if (searchConfig.value.autoSearch) {
		debounceSearch()
	}
}

const handleOrgChange = (originalChange: Function | undefined, val: unknown) => {
	if (originalChange) {
		originalChange(val)
	}
	if (searchConfig.value.autoSearch) {
		debounceSearch()
	}
}

const data = computed(() => {
	return props.form
})
defineExpose({ resetBtn })
</script>

<style scoped lang="scss">
.el-form-item--small {
	--font-size: 12px;
	--el-form-label-font-size: var(--font-size);
	margin-bottom: 6px;
}
</style>
